import React, { useEffect, useRef, useState } from 'react';
import classes from './EditProfilePage2.module.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import SmallLoader from '../../component/SmallLoader';
import { FaArrowLeft, FaCamera, FaTrash, FaCheck } from 'react-icons/fa6';
import { getProfileApi, editProfileApi } from '../../http/http';
import { useTranslation } from 'react-i18next';

const EditProfilePage2 = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [name, setName] = useState('');
    const [pickedImage, setPickedImage] = useState(null); // base64 preview
    const [imageFile, setImageFile] = useState(null); // File object

    const { userDetails } = useSelector(s => s.persisted.userSlice);
    const navigate = useNavigate();
    const fileInput = useRef();

    const { t } = useTranslation();

    /* ── fetch profile ── */
    const fetchProfile = async () => {
        try {
            setLoading(true);
            const r = await getProfileApi(userDetails?.token);
            setProfile(r?.data);
            setName(r?.data?.name ?? '');
        } catch (e) {
            toast.error(
                e?.response?.data?.error?.error ||
                e?.response?.error?.error ||
                e?.response?.error ||
                t("2")
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (userDetails?.token) fetchProfile(); }, [userDetails]);

    /* ── image picker ── */
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = () => setPickedImage(reader.result);
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setPickedImage(null);
        setImageFile(null);
        if (fileInput.current) fileInput.current.value = '';
    };

    /* ── submit ── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaveLoading(true);
            const fd = new FormData();
            fd.append('name', name);
            if (imageFile) fd.append('file', imageFile);
            await editProfileApi(fd, userDetails?.token);
            toast.success(t("257"));
            setPickedImage(null);
            setImageFile(null);
            fetchProfile();
            navigate("/profile");
        } catch (err) {
            toast.error(t("2"));
        } finally {
            setSaveLoading(false);
        }
    };

    /* avatar src resolution */
    const avatarSrc = pickedImage || profile?.image || null;
    const initials = name
        ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
        : '?';

    return (
        <div className={classes.page}>
            <div className={classes.bgGlow} />
            <div className={classes.bgGrid} />

            {/* ══ STICKY HEADER ══ */}
            <div className={classes.stickyHeader}>
                <button
                    type="button"
                    className={classes.backBtn}
                    onClick={() => navigate(-1)}
                    aria-label="Back"
                >
                    <FaArrowLeft />
                </button>
                <div className={classes.headerMid}>
                    <span className={classes.headerTitle}>{t("258")}</span>
                    <span className={classes.headerSub}>{t("259")}</span>
                </div>
                <div style={{ width: 40 }} />
            </div>

            {/* ══ CONTENT ══ */}
            <main className={classes.main}>
                {loading ? (
                    <div className={classes.loaderWrap}>
                        <SmallLoader style={{ border: '4px solid #e60000', borderTopColor: 'transparent' }} />
                        <p className={classes.loaderText}>{t("260")}</p>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        encType="multipart/form-data"
                        className={classes.form}
                    >
                        {/* ── Avatar section ── */}
                        <div className={classes.avatarSection}>
                            {/* ring + image */}
                            <div className={classes.avatarRingOuter}>
                                <div className={classes.avatarRing}>
                                    {avatarSrc ? (
                                        <img
                                            src={avatarSrc}
                                            alt="profile"
                                            className={classes.avatarImg}
                                        />
                                    ) : (
                                        <div className={classes.avatarFallback}>
                                            <span className={classes.avatarInitials}>{initials}</span>
                                        </div>
                                    )}
                                </div>

                                {/* camera FAB */}
                                <button
                                    type="button"
                                    className={classes.cameraFab}
                                    onClick={() => fileInput.current?.click()}
                                    aria-label="Change photo"
                                >
                                    <FaCamera className={classes.cameraIcon} />
                                </button>
                            </div>

                            {/* name below avatar */}
                            <p className={classes.avatarName}>{name || t("261")}</p>

                            {/* photo action buttons */}
                            <div className={classes.photoActions}>
                                <button
                                    type="button"
                                    className={classes.btnUpload}
                                    onClick={() => fileInput.current?.click()}
                                >
                                    <FaCamera style={{ fontSize: 12 }} />
                                    {pickedImage ? t("262") : t("263")}
                                </button>
                                {pickedImage && (
                                    <button
                                        type="button"
                                        className={classes.btnRemove}
                                        onClick={removeImage}
                                    >
                                        <FaTrash style={{ fontSize: 11 }} />
                                        {t("264")}
                                    </button>
                                )}
                            </div>

                            {pickedImage && (
                                <span className={classes.previewNote}>
                                    <FaCheck style={{ fontSize: 9 }} /> {t("265")}
                                </span>
                            )}

                            <input
                                ref={fileInput}
                                type="file"
                                accept="image/png, image/jpeg"
                                className={classes.hiddenInput}
                                onChange={handleImageChange}
                            />
                        </div>

                        {/* ── Fields section ── */}
                        <div className={classes.fieldsSection}>
                            <p className={classes.sectionLabel}>{t("266")}</p>

                            <div className={classes.fieldCard}>

                                {/* Name field */}
                                <div className={classes.fieldGroup}>
                                    <label className={classes.fieldLabel} htmlFor="edit-name">
                                        {t("267")}
                                    </label>
                                    <div className={classes.inputWrap}>
                                        <input
                                            id="edit-name"
                                            type="text"
                                            className={classes.fieldInput}
                                            placeholder={t("268")}
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                            autoComplete="name"
                                        />
                                    </div>
                                </div>

                                {/* Phone (read-only) */}
                                {userDetails?.msisdn && (
                                    <div className={classes.fieldGroup}>
                                        <label className={classes.fieldLabel}>
                                            {t("269")}
                                        </label>
                                        <div className={`${classes.inputWrap} ${classes.inputReadOnly}`}>
                                            <input
                                                type="text"
                                                className={`${classes.fieldInput} ${classes.fieldInputReadOnly}`}
                                                value={userDetails.msisdn}
                                                readOnly
                                            />
                                            <span className={classes.readOnlyBadge}>{t("270")}</span>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* ── Save button ── */}
                        <button
                            type="submit"
                            disabled={saveLoading}
                            className={`${classes.saveBtn} ${!saveLoading ? classes.saveBtnActive : ''}`}
                        >
                            {saveLoading ? (
                                <SmallLoader style={{ width: 20, height: 20, border: '3px solid #1a0000', borderTopColor: 'transparent' }} />
                            ) : (
                                <>
                                    <FaCheck style={{ fontSize: 13 }} />
                                    {t("271")}
                                </>
                            )}
                        </button>

                    </form>
                )}
            </main>
        </div>
    );
};

export default EditProfilePage2;